import useApi from "@/hooks/useApi";
import React, { useMemo, useState } from "react";
import useTeams from "@/hooks/useTeams";
import {
  Column,
  IconPlus,
  PrimaryBlueButton,
  TableBase,
  Team
} from "@velaro/velaro-component-lib";
import ActionBar from "./ActionBar";
import SearchBar from "@/components/SearchBar";
import EditTeamPanel from "./EditTeamPanel";
import CreateTeamPanel from "./CreateTeamPanel";

const columns: Column[] = [
  { label: "Name", accessor: "name" },
  { label: "Id", accessor: "id" },
  { label: "Routing Action", accessor: "routingAction" },
  { label: "Auto Routing Type", accessor: "autoRoutingType" },
  { label: "Unassignment Action", accessor: "unassignmentAction" },
  { label: "Auto Unassignment Warning", accessor: "autoUnassignmentWarning" },
  { label: "Actions", accessor: "actions", disableSort: true }
];

export default function Teams() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTeamId, setEditTeamId] = useState<number>();
  const [searchString, setSearchString] = useState("");
  const [selectedIndicies, setSelectedIndicies] = useState<number[]>([]);
  const api = useApi();

  const { teams, setTeams } = useTeams();

  async function deleteTeam(id: number) {
    if (teams.length === 1) {
      alert("Unable to delete team. You must always have at least one team.");
      return;
    }
    if (
      confirm(
        "Are you sure you want to delete this team? Deleting this team could cause users to no longer be apart of a team."
      )
    ) {
      await api.messaging.delete(`Teams/${id}`);
      setTeams(teams.filter((x) => x.id !== id));
    }
  }

  const filteredTeams = useMemo(
    () =>
      teams.filter((val) => {
        if (searchString === "") {
          return val;
        } else if (
          val.name.toLowerCase().includes(searchString.toLowerCase()) ||
          val.id
            .toString()
            .toLowerCase()
            .includes(searchString.toLowerCase()) ||
          val.routingAction
            .toLowerCase()
            .includes(searchString.toLowerCase()) ||
          val.routingType.toLowerCase().includes(searchString.toLowerCase()) ||
          val.unassignAction
            .toLowerCase()
            .includes(searchString.toLowerCase()) ||
          val.unassignWarningTime
            .toString()
            .toLowerCase()
            .includes(searchString.toLowerCase())
        ) {
          return val;
        }
      }),
    [teams, searchString]
  );

  const tableData = useMemo(() => {
    return filteredTeams.map((team) => ({
      ...team,
      name: team.name,
      id: team.id,
      routingAction: team.routingAction,
      autoRoutingType: team.routingType,
      unassignmentAction: team.unassignAction,
      autoUnassignmentWarning: team.unassignWarningTime,
      actions: (
        <>
          <ActionBar
            onEdit={() => setEditTeamId(team.id)}
            onCopy={() => {}}
            onDelete={() => deleteTeam(team.id!)}
          />
        </>
      )
    }));
  }, [filteredTeams, editTeamId, deleteTeam]);
  return (
    <>
      {editTeamId === undefined && showCreateModal === false && (
        <>
          <div className="ml-auto">
            <div className="pb-4">
              <div className="flex justify-between items-center mb-4">
                <div className="text-lg font-medium text-slate-800">Teams</div>
                <div className="flex items-center justify-end pb-4 mt-2 px-4">
                  <div className="flex px-4">
                    <SearchBar
                      onSearch={setSearchString}
                      placeholderText="Search"
                    />
                  </div>
                  <div>
                    <PrimaryBlueButton
                      prefixIcon={<IconPlus />}
                      onClick={() => setShowCreateModal(true)}
                      label="Add Team"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-auto h-full">
            <TableBase
              columns={columns}
              data={tableData}
              onChangeSelection={setSelectedIndicies}
            />
          </div>
        </>
      )}

      {showCreateModal && (
        <CreateTeamPanel
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onTeamCreated={(team: Team) => {
            setShowCreateModal(false);
            setTeams([...teams, team]);
          }}
        />
      )}
      {editTeamId && (
        <EditTeamPanel
          teamId={editTeamId}
          onClose={() => setEditTeamId(undefined)}
          onTeamUpdated={(team: Team) => {
            setEditTeamId(undefined);
            setTeams(teams.map((x) => (x.id === team.id ? team : x)));
          }}
        />
      )}
    </>
  );
}

const thClassName = "border p-2 text-left";
const tdClassName = "border p-2";
